import React from "react";
import { Row, Col, Card, Badge, Button } from "react-bootstrap";

interface Props {
  currentAnnee: any;
  annees: any[];
  backups: any[];
  migrationDone: boolean;
  onGoToWizard: () => void;
  onGoToBackups: () => void;
  onGoToArchives: () => void;
}

const DashboardTab: React.FC<Props> = ({
  currentAnnee,
  annees,
  backups,
  migrationDone,
  onGoToWizard,
  onGoToBackups,
  onGoToArchives,
}) => {
  const archivedCount = annees.filter((a) => !a.isCurrent).length;
  const lastBackup = backups.length > 0 ? backups[0] : null;
  const daysSinceBackup = lastBackup
    ? Math.floor((Date.now() - new Date(lastBackup.date).getTime()) / 86400000)
    : null;

  return (
    <>
      {/* Current year header */}
      {currentAnnee && (
        <Card className="border-primary border-opacity-25 mb-4">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h5 className="mb-1">
                  <i className="ph ph-calendar-check me-2 text-primary"></i>
                  Année en cours : <strong>{currentAnnee.label}</strong>
                </h5>
                <span className="text-muted">
                  {currentAnnee.date_debut
                    ? `Depuis le ${new Date(currentAnnee.date_debut).toLocaleDateString("fr-FR")}`
                    : "Date de début non définie"}
                </span>
              </div>
              <Button variant="warning" onClick={onGoToWizard}>
                <i className="ri-archive-line me-1"></i>
                Lancer la fin d'année
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* KPI cards */}
      <Row className="g-3 mb-4">
        <Col sm={6} xl={3}>
          <Card className="card-animate">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="avatar-sm flex-shrink-0">
                  <span className="avatar-title bg-info-subtle text-info rounded-circle fs-4">
                    <i className="ph ph-database"></i>
                  </span>
                </div>
                <div className="flex-grow-1 ms-3">
                  <p className="text-uppercase fw-semibold text-muted mb-1 fs-12">Sauvegardes</p>
                  <h4 className="mb-0">{backups.length}</h4>
                  {daysSinceBackup !== null && (
                    <span className={`fs-12 ${daysSinceBackup > 7 ? "text-danger" : "text-muted"}`}>
                      {daysSinceBackup === 0
                        ? "Dernier : aujourd'hui"
                        : `Dernier : il y a ${daysSinceBackup}j`}
                      {daysSinceBackup > 7 && " ⚠"}
                    </span>
                  )}
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col sm={6} xl={3}>
          <Card className="card-animate">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="avatar-sm flex-shrink-0">
                  <span className="avatar-title bg-warning-subtle text-warning rounded-circle fs-4">
                    <i className="ph ph-archive"></i>
                  </span>
                </div>
                <div className="flex-grow-1 ms-3">
                  <p className="text-uppercase fw-semibold text-muted mb-1 fs-12">Années archivées</p>
                  <h4 className="mb-0">{archivedCount}</h4>
                  <button
                    className="btn btn-link p-0 fs-12"
                    onClick={onGoToArchives}
                  >
                    Consulter les archives
                  </button>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col sm={6} xl={3}>
          <Card className="card-animate">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="avatar-sm flex-shrink-0">
                  <span className="avatar-title bg-success-subtle text-success rounded-circle fs-4">
                    <i className="ph ph-check-circle"></i>
                  </span>
                </div>
                <div className="flex-grow-1 ms-3">
                  <p className="text-uppercase fw-semibold text-muted mb-1 fs-12">Migration</p>
                  <h4 className="mb-0">
                    {migrationDone ? (
                      <Badge bg="success">Effectuée</Badge>
                    ) : (
                      <Badge bg="warning">En attente</Badge>
                    )}
                  </h4>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col sm={6} xl={3}>
          <Card className="card-animate">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="avatar-sm flex-shrink-0">
                  <span className="avatar-title bg-primary-subtle text-primary rounded-circle fs-4">
                    <i className="ph ph-calendar-blank"></i>
                  </span>
                </div>
                <div className="flex-grow-1 ms-3">
                  <p className="text-uppercase fw-semibold text-muted mb-1 fs-12">Total années</p>
                  <h4 className="mb-0">{annees.length}</h4>
                  <span className="fs-12 text-muted">
                    {archivedCount} archivée{archivedCount > 1 ? "s" : ""}
                  </span>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Quick actions */}
      <Card>
        <Card.Header>
          <h6 className="card-title mb-0">Actions rapides</h6>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={4}>
              <div
                className="border rounded p-3 text-center cursor-pointer"
                style={{ cursor: "pointer" }}
                onClick={onGoToBackups}
              >
                <i className="ri-hard-drive-2-line fs-2 text-info"></i>
                <h6 className="mt-2 mb-1">Créer un backup</h6>
                <p className="text-muted small mb-0">Sauvegarde manuelle de la base de données</p>
              </div>
            </Col>
            <Col md={4}>
              <div
                className="border rounded p-3 text-center"
                style={{ cursor: "pointer" }}
                onClick={onGoToArchives}
              >
                <i className="ri-folder-open-line fs-2 text-warning"></i>
                <h6 className="mt-2 mb-1">Consulter les archives</h6>
                <p className="text-muted small mb-0">Données des années précédentes</p>
              </div>
            </Col>
            <Col md={4}>
              <div
                className="border rounded p-3 text-center"
                style={{ cursor: "pointer" }}
                onClick={onGoToWizard}
              >
                <i className="ri-skip-forward-line fs-2 text-danger"></i>
                <h6 className="mt-2 mb-1">Fin d'année</h6>
                <p className="text-muted small mb-0">Processus guidé de clôture</p>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </>
  );
};

export default DashboardTab;
